import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface KoryoProps {
  selectedBelt: string;
  getBeltColor: (belt: string) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

const PATTERN_INFO_TEXT = (
  <>
    <p className="mb-4 text-gray-700">
      Koryo (Korea) is the name of an old Korean Dynasty. The people from the Koryo-period defeated the Mongolian aggressors. Their spirit is
      reflected in the movements of the Poomse Koryo. Each movement of this Poomse represents the strength and energy needed to control the Mongols.
    </p>
  </>
);

function KoryoPatternSequence() {
  return (
    <ol className="ml-6 list-decimal list-outside space-y-3 text-base marker:text-taekwondo-red">
      <li className="pl-2 text-gray-700">
        From ready stance, bring your hands to the front of your face, forming a triangle, Turn left 90 degrees by sliding your left foot into a
        right Back Stance and execute a mid section Knife Hand Guarding Block
      </li>
      <li className="pl-2 text-gray-700">
        Execute a 2 section side kick with the right leg (knee/high) Drop into a right forward stance while executing a right knife hand strike
      </li>
      <li className="pl-2 text-gray-700">Keep the same stance and execute a mid section reverse punch</li>
      <li className="pl-2 text-gray-700">Adjust right leg into a back stance while executing a inner hammer block with the right hand</li>
      <li className="pl-2 text-gray-700">Turn 180 degrees to the right into Back stance Knife hand Guarding Blk</li>
      <li className="pl-2 text-gray-700">
        Execute a 2 section side kick with the left leg (knee/high) Drop into a left forward stance while executing a left knife hand strike
      </li>
      <li className="pl-2 text-gray-700">Keep the same stance and execute a mid section reverse punch</li>
      <li className="pl-2 text-gray-700">Adjust left leg into a back stance while executing a inner hammer block with the left hand</li>
      <li className="pl-2 text-gray-700">
        Turn 90 degrees to your left into a left front stance while executing a low section Knife Hand Block with the left arm followed by an
        Arc-Hand strike to the neck with the right
      </li>
      <li className="pl-2 text-gray-700">
        Front kick with the right leg dropping into a right forward stance while executing a low section Knife Hand Block with the right arm followed
        by an Arc-Hand strike to the neck with the left
      </li>
      <li className="pl-2 text-gray-700">
        Front kick with the left leg dropping into a left forward stance while executing a low section Knife Hand Block with the left arm followed by
        an Arc-Hand strike to the neck with the right <span className="font-bold">KIHAP</span>
      </li>
      <li className="pl-2 text-gray-700">
        Front kick with the right leg dropping into a right forward stance while executing a knee break (low cupped strike to the knee) with the left
        hand
      </li>
      <li className="pl-2 text-gray-700">
        Step forward and turn about to face opposite direction into a right front stance while executing a reverse mid section wedging block
      </li>
      <li className="pl-2 text-gray-700">
        Front kick with the left leg dropping into a left front stance while executing a knee break (low cupped strike to the knee) with the right
        hand
      </li>
      <li className="pl-2 text-gray-700">
        Move the left foot back into a left walking stance while executing a reverse mid section wedging block (tension)
      </li>
      <li className="pl-2 text-gray-700">
        Pivoting on the left foot turn right 180 degrees into Horse Stance executing a mid section Knife-Hand block with the left
      </li>
      <li className="pl-2 text-gray-700">Keep in same stance, execute a right punch to the palm of the left hand</li>
      <li className="pl-2 text-gray-700">
        Cross right foot in front of left while executing a rear elbow strike and left leg side kick drop into a right forward stance (facing opposite
        direction) while executing a spear finger thrust to the groin area (palm up)
      </li>
      <li className="pl-2 text-gray-700">Move the right foot back into a right walking stance and execute a down block with the right hand</li>
      <li className="pl-2 text-gray-700">
        Step Left foot forward into a left walking stance and execute a palm block with the left hand. Pivot on the left foot move the right foot
        forward into Horse Stance executing a right elbow strike
      </li>
      <li className="pl-2 text-gray-700">Keep in same stance and execute a mid section Knife-Hand blockwith the right</li>
      <li className="pl-2 text-gray-700">Keep in same stance, execute a left punch to the palm of the right hand</li>
      <li className="pl-2 text-gray-700">
        Cross left foot in front of right while executing a rear elbow strike and right leg side kick drop into a left forward stance (facing opposite
        direction) while executing a spear finger thrust to the groin area (palm up)
      </li>
      <li className="pl-2 text-gray-700">Move the left foot back into a left walking stance and execute a down block with the left hand</li>
      <li className="pl-2 text-gray-700">
        Step right foot forward into a right walking stance and execute a palm block with the right hand. Pivot on the right foot move the left foot
        forward into Horse Stance executing a tension elbow strike with the left arm
      </li>
      <li className="pl-2 text-gray-700">
        Move the to the right into ready stance (feet together). Raise both hands in a circular motion, bring them down in front of the abdomen with
        the Left fist (palm up) against right open palm
      </li>
      <li className="pl-2 text-gray-700">
        Pivot on the right foot turn left 180 degrees into a left front stance execute a Knife hand strike with the left hand followed with a low knife
        hand with the left hand
      </li>
      <li className="pl-2 text-gray-700">
        Step right leg forward into front stance execute a right hand reverse knife hand strike followed by a low knife hand with the right hand
      </li>
      <li className="pl-2 text-gray-700">
        Step left leg forward into front stance execute a left hand reverse knife hand strike followed by a low knife hand with the left hand
      </li>
      <li className="pl-2 text-gray-700">
        Step right foot forward into front stance and execute a right hand arc-hand strike <span className="font-bold">KIHAP</span> (pivot on the
        right foot, turn left 180 degrees and return to start position hands in front of face in triangle position)
      </li>
    </ol>
  );
}

export default function Koryo({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: KoryoProps) {
  if (!selectedBelt.startsWith("BLACK")) {
    return null;
  }

  const surface = beltTabSurfaceClass;
  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const demoBtn =
    hasDemo?.(["koryo"]) ? (
      <button
        type="button"
        className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
        onClick={() => onWatchDemo?.(["koryo"])}
      >
        Watch Demonstration Video
      </button>
    ) : null;

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">Koryo</h2>
        {demoBtn && <div className="mb-3">{demoBtn}</div>}
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="Koryo">
            <TabsTrigger value="pattern-information" className={triggerClass}>
              Pattern Information
            </TabsTrigger>
            <TabsTrigger value="pattern" className={triggerClass}>
              Pattern
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              {PATTERN_INFO_TEXT}
            </div>
          </TabsContent>
          <TabsContent value="pattern" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Sequence</h4>
              <KoryoPatternSequence />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        <div>
          {demoBtn && <div className="mb-3">{demoBtn}</div>}
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
          {PATTERN_INFO_TEXT}
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Sequence</h4>
          <KoryoPatternSequence />
        </div>
      </div>
    </div>
  );
}
